import { useState } from 'react';

import { Badge } from '@/shared/ui/badge';
import { Breadcrumb } from '@/shared/ui/breadcrumb';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Field } from '@/shared/ui/field';
import { Icon, PHX_ICONS } from '@/shared/ui/icon';
import { Input } from '@/shared/ui/input';
import { LabelBadge } from '@/shared/ui/label-badge';
import { Panel, PanelGroup } from '@/shared/ui/panel';
import { Radio } from '@/shared/ui/radio';
import { Select } from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import { Tabs } from '@/shared/ui/tabs';
import { Textarea } from '@/shared/ui/textarea';

export function AtomsSection() {
  const [tab, setTab] = useState('series');
  const [pill, setPill] = useState('all');
  const [seg, setSeg] = useState('compact');
  const [radio, setRadio] = useState('one');
  const [bool, setBool] = useState(true);
  const [check, setCheck] = useState(true);

  return (
    <PanelGroup gap={12}>
      <Panel title="Buttons" icon="edit">
        <div className="gap-phx-3 flex flex-wrap items-center">
          <Button variant="primary">Save</Button>
          <Button variant="default">Cancel</Button>
          <Button variant="outline">Detach</Button>
          <Button variant="ghost">Skip</Button>
          <Button variant="link">Learn more</Button>
          <Button variant="danger">Delete</Button>
          <Button variant="success" iconLeft="ok">
            Approve
          </Button>
          <Button variant="primary" iconLeft="plus">
            Add stop
          </Button>
          <Button variant="default" iconRight="chevdown">
            More
          </Button>
          <Button variant="ghost" size="icon" iconLeft="morev" aria-label="More" />
          <Button variant="primary" loading>
            Saving
          </Button>
          <Button variant="primary" active>
            Active
          </Button>
        </div>
        <div className="mt-phx-5 gap-phx-3 flex flex-wrap items-end">
          <Button size="sm">SM</Button>
          <Button size="md">MD</Button>
          <Button size="lg">LG</Button>
          <Button size="xl">XL</Button>
        </div>
      </Panel>

      <Panel title="Inputs & fields" icon="edit">
        <div className="gap-phx-5 grid grid-cols-2">
          <Field label="Default">
            <Input placeholder="Type here…" />
          </Field>
          <Field label="With icons">
            <Input leadingIcon="search" trailingIcon="help" placeholder="Search…" />
          </Field>
          <Field label="Required" required>
            <Input required placeholder="Must be filled" />
          </Field>
          <Field label="Invalid" error="Value cannot be empty">
            <Input invalid defaultValue="" placeholder="…" />
          </Field>
          <Field label="Draft">
            <Input draft defaultValue="Unsaved value" />
          </Field>
          <Field label="Select">
            <Select options={['Option A', 'Option B', 'Option C']} placeholder="Select…" />
          </Field>
          <Field label="Textarea" className="col-span-2">
            <Textarea placeholder="Multi-line input…" rows={3} />
          </Field>
        </div>
      </Panel>

      <Panel title="Choice controls" icon="ok">
        <div className="gap-phx-5 grid grid-cols-3">
          <div className="gap-phx-3 flex flex-col">
            <Checkbox label="Unchecked" checked={false} onCheckedChange={() => {}} />
            <Checkbox label="Checked" checked={check} onCheckedChange={setCheck} />
            <Checkbox label="Indeterminate" indeterminate />
            <Checkbox label="Disabled" disabled />
          </div>
          <div className="gap-phx-3 flex flex-col">
            <Radio
              name="demo"
              value="one"
              checked={radio === 'one'}
              onChange={() => setRadio('one')}
              label="Option one"
            />
            <Radio
              name="demo"
              value="two"
              checked={radio === 'two'}
              onChange={() => setRadio('two')}
              label="Option two"
            />
            <Radio name="demo" value="three" disabled label="Disabled" />
          </div>
          <div className="gap-phx-3 flex flex-col">
            <Switch checked={bool} onCheckedChange={setBool} label="Notifications" />
            <Switch checked={false} label="Off" />
            <Switch checked disabled label="Disabled" />
          </div>
        </div>
      </Panel>

      <Panel title="Badges" icon="star">
        <div className="gap-phx-3 flex flex-wrap">
          <Badge tone="neutral">Neutral</Badge>
          <Badge tone="primary">Primary</Badge>
          <Badge tone="success" leadingIcon="ok">
            Confirmed
          </Badge>
          <Badge tone="warning" leadingIcon="alert">
            Pending
          </Badge>
          <Badge tone="danger" leadingIcon="x">
            Cancelled
          </Badge>
          <Badge tone="info">Info</Badge>
          <Badge tone="draft">Unsaved</Badge>
          <Badge tone="primary" appearance="solid">
            Solid
          </Badge>
          <Badge tone="success" appearance="outline">
            Outline
          </Badge>
          <Badge tone="success" disabled>
            Disabled
          </Badge>
        </div>
        {/* Solid varianta pro každý tón — dřív se ukazoval jen `primary`, takže změna
            foreground barvy u ostatních tónů nebyla nikde vidět (ani ve vizuální baseline). */}
        <div className="gap-phx-3 mt-phx-3 flex flex-wrap">
          {(['neutral', 'primary', 'success', 'warning', 'danger', 'info', 'draft'] as const).map(
            (tone) => (
              <Badge key={tone} tone={tone} appearance="solid">
                {tone}
              </Badge>
            )
          )}
        </div>
      </Panel>

      <Panel title="Label badges" icon="star">
        <div className="gap-phx-2 flex flex-wrap items-center">
          <LabelBadge tone="success" label="General & Terms" status="APPROVED" />
          <LabelBadge tone="warning" label="Main Transport Scheme" status="IN PREPARATION" />
          <LabelBadge tone="danger" label="Invoice" status="CANCELLED" />
          <LabelBadge tone="neutral" label="Contract" status="DRAFT" />
          <LabelBadge tone="info" label="Payment" status="SYNCED" />
          <LabelBadge tone="success" label="General & Terms" status="APPROVED" disabled />
        </div>
      </Panel>

      <Panel title="Tabs" icon="layers">
        <div className="gap-phx-5 flex flex-col">
          <Tabs
            value={tab}
            onChange={setTab}
            items={[
              { value: 'series', label: 'Series' },
              { value: 'terms', label: 'Terms', count: 17 },
              { value: 'people', label: 'People', count: 245 },
              { value: 'pricing', label: 'Pricing' },
            ]}
          />
          <Tabs
            variant="segmented"
            value={seg}
            onChange={setSeg}
            items={['compact', 'default', 'comfortable']}
          />
          <Tabs
            variant="pills"
            value={pill}
            onChange={setPill}
            items={[
              { value: 'all', label: 'All', count: 14 },
              { value: 'active', label: 'Active', count: 8 },
              { value: 'archived', label: 'Archived', count: 6 },
            ]}
          />
        </div>
      </Panel>

      <Panel title="Breadcrumb" icon="map">
        <Breadcrumb
          items={[
            { label: 'Destinations', href: '#' },
            { label: 'Mallorca', href: '#' },
            { label: 'Series detail' },
          ]}
        />
      </Panel>

      <Panel title="Icon set" icon="layers">
        <div className="gap-phx-4 grid grid-cols-8">
          {(Object.keys(PHX_ICONS) as Array<keyof typeof PHX_ICONS>).map((name) => (
            <div
              key={name}
              className="gap-phx-2 rounded-phx-sm border-phx-border p-phx-3 text-phx-xs text-phx-text-muted flex flex-col items-center border"
            >
              <Icon name={name} size={16} className="text-phx-text" />
              <span>{name}</span>
            </div>
          ))}
        </div>
      </Panel>
    </PanelGroup>
  );
}
